import { useCallback, useEffect, useRef } from "react";
import type { RefObject } from "react";
import debounce from "lodash/debounce";
import { v4 as uuidv4 } from "uuid";
import type { PropertiesType } from "@/react/api/analyticsTrackerApi";
import { analyticsTrackerApi } from "@/react/api/analyticsTrackerApi";
import { usePunditUserContext } from "@/react/contexts";
import { usePageViewPropertiesContext } from "./pageViewPropertiesContext";

const MINIMUM_WATCH_THRESHOLD = 30;

interface VideoViewProperties extends PropertiesType {
  community_id?: number;
  community_member_id?: number;
  video_url: string;
  video_duration: number;
  video_view_id: string;
  page_view_id: string;
  signed_id?: string;
}

export const useVideoAnalytics = (
  videoRef: RefObject<HTMLVideoElement>,
  signedId?: string | undefined,
) => {
  const viewIdRef = useRef(uuidv4());
  const hasTrackedRef = useRef(false);
  const { pageViewId } = usePageViewPropertiesContext();
  const {
    masqueradingEntity,
    currentCommunity,
    currentCommunityMember,
    isLoading,
  } = usePunditUserContext();

  const communityId = currentCommunity?.id;
  const communityMemberId = currentCommunityMember?.id;
  const shouldSkipAnalytics = !!(masqueradingEntity || isLoading || !signedId);

  const trackVideoView = useCallback(async () => {
    if (shouldSkipAnalytics || !videoRef.current || hasTrackedRef.current)
      return;

    const video = videoRef.current;

    const properties: VideoViewProperties = {
      community_id: communityId,
      community_member_id: communityMemberId,
      video_url: video.currentSrc,
      video_duration: Math.round(video.duration),
      video_view_id: viewIdRef.current,
      page_view_id: pageViewId ?? "unknown",
      signed_id: signedId,
    };

    await analyticsTrackerApi.trackVideoView(properties);
    hasTrackedRef.current = true;
  }, [
    communityId,
    communityMemberId,
    shouldSkipAnalytics,
    videoRef,
    signedId,
    pageViewId,
  ]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (!signedId) return;

    const video = videoRef.current;

    const debouncedOnTimeUpdate = debounce(() => {
      if (hasTrackedRef.current) return;

      const videoDuration = video.duration;
      const currentTime = video.currentTime;

      const isShortVideoStarted =
        videoDuration <= MINIMUM_WATCH_THRESHOLD && currentTime > 0;
      const isLongVideoThresholdMet =
        videoDuration > MINIMUM_WATCH_THRESHOLD &&
        currentTime >= MINIMUM_WATCH_THRESHOLD;

      if (isShortVideoStarted || isLongVideoThresholdMet) {
        void trackVideoView();
      }
    }, 250);

    video.addEventListener("timeupdate", debouncedOnTimeUpdate);

    return () => {
      debouncedOnTimeUpdate.cancel();
      video.removeEventListener("timeupdate", debouncedOnTimeUpdate);
    };
  }, [trackVideoView, videoRef, signedId, pageViewId]);
};
